import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FiFileText, FiCreditCard, FiHome } from 'react-icons/fi';
import Header from '../../components/header';
import Footer from '../../components/footer';
import CustomModal from '../../components/Animation/CustomModal';
import { InputField, ButtonRaw } from '../../components/formsField';
import { sendEmail } from '../../NetworkServices/services';
import emailData from '../loans/Component/emailData';
import familyImage from '../../images/school-fees-hero.jpg';
import heroPattern from '../../images/current-account/hero-pattern.svg';
import styles from './index.module.css';

const benefits = [
  'Borrow from ₦30,000 – ₦1,000,000',
  'Low 3.5% monthly interest (reducing balance)',
  'Flexible 6–12 month repayment',
  'Just 1% management + 1% processing fee',
  'Designed for parents/guardians in private / government schools',
];
const requirements = [
  'School fees bill & ID letter from school',
  'Valid KYC Documents',
  'Open/maintain an account with Zikora MFB',
];
const fields = [
  ['userName', 'Full Name', 'Enter Full Name'],
  ['email', 'Email', 'Enter Email'],
  ['phone', 'Phone', 'Enter Phone Number'],
  ['address', 'Address', 'Enter Address'],
  ['amount', 'Loan Amount', 'Enter Amount'],
  ['purpose', 'Loan Purpose', 'Enter Purpose'],
];
const schema = Yup.object({
  userName: Yup.string().required('Full Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  amount: Yup.string().required('Loan Amount is required'),
  purpose: Yup.string().required('Loan Purpose is required'),
});
const Label = ({ children, light = false }) => <span className={styles.label + (light ? ' ' + styles.labelLight : '')}><i aria-hidden="true" />{children}</span>;

export default function SchoolFeesLoan() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: { userName: '', email: '', phone: '', address: '', amount: '', purpose: '' },
    validationSchema: schema,
    onSubmit: async (values, actions) => {
      setLoading(true);
      try {
        const response = await sendEmail(emailData(values, 'customer.service@zikoramfb.com'));
        if (response.status === 200) {
          actions.resetForm();
          setOpen(false);
          toast.success(response.data?.message);
        } else toast.error(response.data?.message);
      } catch (error) {
        toast.error('We could not submit your application. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });
  const close = () => { if (!loading) { formik.resetForm(); setOpen(false); } };
  const apply = () => setOpen(true);

  return <main className={styles.page}>
    <Header solid compactMobile />
    <section className={styles.hero} style={{ '--hero-pattern': `url(${heroPattern})` }}>
      <div className={styles.heroInner}>
        <div className={styles.heroCopy}>
          <Label light>Loans</Label>
          <h1>Your <em>Back to <br className={styles.mobileBreak} /><span className={styles.schoolWord}>School</span></em> Loan is ready<span className={styles.mobilePeriod}>.</span></h1>
          <p>Get up to 1,000,000 today!</p>
          <button className={styles.button} onClick={apply}>Click here to apply</button>
        </div>
        <div className={styles.heroImage}><img src={familyImage} alt="Parent with children on their way to school" /></div>
      </div>
    </section>
    <section className={styles.benefitsSection}>
      <div className={styles.contentWidth}>
        <Label>School fees loan</Label>
        <h2>Why Parents Choose Zikora MFB School Fees Loan</h2>
        <ol className={styles.benefits}>{benefits.map((benefit, index) => <li key={benefit}><span>0{index + 1}</span><p>{benefit}</p></li>)}</ol>
      </div>
    </section>
    <section className={styles.requirementsSection}>
      <div className={styles.contentWidth}>
        <div className={styles.sectionTitle}><Label>Requirements</Label><h2>Requirements to get started</h2></div>
        <div className={styles.requirements}>{requirements.map((item, index) => {
          const Icon = [FiFileText, FiCreditCard, FiHome][index];
          return <article key={item}><span className={styles.requirementIcon} aria-hidden="true"><Icon /></span><p>{item}</p></article>;
        })}</div>
      </div>
    </section>
    <section className={styles.ctaSection}><div className={styles.cta}><h2>Get up to <em>1,000,000</em><br /> today!</h2><button className={styles.button} onClick={apply}>Click here to apply</button></div></section>
    <Footer about />
    <CustomModal modalClassName="overflow-y-auto max-h-[95vh]" parentContainer="!mt-4 sm:!mt-6" open={open} handleClose={close}>
      {loading ? <div className="h-[300px] flex justify-center items-center"><CircularProgress /></div> : <div className="flex flex-col gap-5 mb-4">
        <p className="text-center">Submit Details</p>
        <div className="flex flex-col gap-3">
          {fields.map(([name, label, placeholder]) => <div className="flex flex-col gap-1" key={name}>
            <label className="text-[12px]" htmlFor={name}>{label}</label>
            <InputField className="border-1 rounded-sm py-2 text-[14px]" name={name} formik={formik} title={placeholder} showError multiline={name === 'purpose'} rows={name === 'purpose' ? 3 : undefined} />
          </div>)}
        </div>
        <div className="flex justify-between"><ButtonRaw text="Cancel" variants="outlined" onClick={close} /><ButtonRaw text="Send" onClick={formik.handleSubmit} /></div>
      </div>}
    </CustomModal>
  </main>;
}
